import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Cross-tab record-change sync.
 *
 * When a record is mutated in one tab (visibility toggle, create, or a
 * grid-relevant detail edit), that tab broadcasts a small message on a shared
 * BroadcastChannel. Other tabs showing the same list pick it up and, if the
 * change actually affects what they render, flip an `isStale` flag so the grid
 * can surface a "Refresh" affordance.
 *
 * Detection is done per-list via the `entity` tag and a grid-supplied
 * `isRelevant(msg)` callback, so a tab only reacts to changes that would alter
 * its own visible cards.
 */

const CHANNEL_NAME = "osw-records";

// Stable per-tab id so a tab can ignore its own broadcasts.
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Lazily-created singleton channel, shared by the hook and the standalone
// helper so both speak on the same channel from anywhere in the app.
let sharedChannel = null;
const getChannel = () => {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!sharedChannel) sharedChannel = new BroadcastChannel(CHANNEL_NAME);
  return sharedChannel;
};

/**
 * Announce a record change. Usable outside React (e.g. the service layer).
 * @param {"archive"|"videos"|"libraries"} entity
 * @param {{id?: string, action: "create"|"update"|"visibility", snapshot?: object}} payload
 */
export const broadcastRecordChange = (entity, payload = {}) => {
  const ch = getChannel();
  if (!ch) return;
  const { id, action, snapshot } = payload;
  ch.postMessage({ entity, id, action, snapshot, tabId: TAB_ID });
};

/**
 * Default relevance check for grid lists: does an incoming change alter what
 * this grid currently renders?
 * - create: assume yes (a new card may belong on this view; can't be sure
 *   without reloading).
 * - update/visibility: only if the changed record is currently shown AND a
 *   visible field we were told about actually differs.
 * @param {object} msg broadcast message
 * @param {Array} records the grid's current records
 */
export const isGridChangeRelevant = (msg, records = []) => {
  if (!msg) return false;
  if (msg.action === "create") return true;

  const match = records.find(
    (r) => (r.alias || r._id || r.id) === msg.id
  );
  if (!match) return false;

  const snap = msg.snapshot || {};
  return Object.keys(snap).some(
    (k) => snap[k] !== undefined && snap[k] !== match[k]
  );
};

/**
 * @param {"archive"|"videos"|"libraries"} entity
 * @param {(msg: object) => boolean} [isRelevant] decides whether an incoming
 *   change affects this grid's visible cards. Defaults to always relevant.
 * @returns {{ isStale: boolean, broadcastChange: Function, markRefreshed: Function }}
 */
export const useRecordSync = (entity, isRelevant) => {
  const [isStale, setIsStale] = useState(false);

  // Keep the latest isRelevant in a ref so the channel listener always sees
  // current records without re-subscribing on every render.
  const isRelevantRef = useRef(isRelevant);
  isRelevantRef.current = isRelevant;

  useEffect(() => {
    const ch = getChannel();
    if (!ch) return undefined;

    const handler = (e) => {
      const msg = e?.data;
      if (!msg || msg.entity !== entity || msg.tabId === TAB_ID) return;
      const relevant = isRelevantRef.current ? isRelevantRef.current(msg) : true;
      if (relevant) setIsStale(true);
    };

    ch.addEventListener("message", handler);
    return () => ch.removeEventListener("message", handler);
  }, [entity]);

  const broadcastChange = useCallback(
    (payload) => broadcastRecordChange(entity, payload),
    [entity]
  );

  const markRefreshed = useCallback(() => setIsStale(false), []);

  return { isStale, broadcastChange, markRefreshed };
};
